import type { ReactNode } from 'react'
import { useRef, useState } from 'react'
import {
  Bold01Icon,
  Italic01Icon,
  ListIcon,
  NumberedListBulletsIcon,
  Save01Icon,
} from '@systemeio/icons'
import type { Editor } from '@tiptap/core'
import { BubbleMenu } from '@tiptap/react/menus'
import BlockButton from './block-button'
import LinkButton from './link-button'
import MarkButton from './mark-button'

export default function HoveringToolbar({ editor }: { editor: Editor }): ReactNode {
  const [linkIsEditing, setLinkIsEditing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const linkRangeRef = useRef<{ from: number; to: number } | null>(null)

  const finishLinkEdit = (): void => {
    setLinkIsEditing(false)
    linkRangeRef.current = null
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleSaveLink = (): void => {
    const url = inputRef.current?.value.trim()
    const range = linkRangeRef.current
    if (url && range) {
      editor
        .chain()
        .focus()
        .setTextSelection(range)
        .extendMarkRange('link')
        .setLink({ href: url })
        .run()
    }
    finishLinkEdit()
  }

  return (
    <BubbleMenu
      editor={editor}
      options={{ placement: 'top' }}
      shouldShow={({ editor: ed, state }) => {
        if (linkIsEditing) return true
        const { empty, from, to } = state.selection
        return (
          ed.isEditable && ed.isFocused && !empty && ed.state.doc.textBetween(from, to).length > 0
        )
      }}
      className="z-[100] rounded-lg bg-bg-primary p-2 shadow-2"
    >
      {!linkIsEditing && (
        <div data-testid="post-bubble-menu">
          <MarkButton editor={editor} format="bold" label="bold">
            <Bold01Icon height="15px" />
          </MarkButton>
          <MarkButton editor={editor} format="italic" label="italic">
            <Italic01Icon height="15px" />
          </MarkButton>
          <BlockButton editor={editor} format="bulletList" label="bulleted-list">
            <ListIcon height="15px" />
          </BlockButton>
          <BlockButton editor={editor} format="orderedList" label="numbered-list">
            <NumberedListBulletsIcon height="15px" />
          </BlockButton>
          <LinkButton
            editor={editor}
            label="link"
            onStartLinkEdit={() => {
              const { from, to } = editor.state.selection
              if (from === to) return
              linkRangeRef.current = { from, to }
              setLinkIsEditing(true)
            }}
          />
        </div>
      )}
      {linkIsEditing && (
        <div className="flex" data-testid="post-bubble-menu-link">
          <input
            className="block w-32 border-2"
            ref={inputRef}
            data-testid="post-link-url"
            autoFocus
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault()
                handleSaveLink()
              }
              if (event.key === 'Escape') {
                event.preventDefault()
                finishLinkEdit()
                editor.chain().focus().run()
              }
            }}
            onMouseDown={event => event.stopPropagation()}
          />
          <button
            type="button"
            data-testid="post-link-save"
            className="ml-1 cursor-pointer self-center"
            onMouseDown={event => {
              event.preventDefault()
              event.stopPropagation()
              handleSaveLink()
            }}
          >
            <Save01Icon className="text-text-secondary" height="20px" />
          </button>
        </div>
      )}
    </BubbleMenu>
  )
}
